import { Button, Card, Empty, Space } from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import dayjs from 'dayjs';
import 'dayjs/locale/id';
import { apiCall } from '../../API/RestApi';

dayjs.locale('id');

function AssetReceiptPreview() {
    const token = useSelector((state) => state.user.token);
    const printRef = useRef(null);
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const id = location.state?.id || searchParams.get('id');
    const receiptType = location.state?.type || searchParams.get('type') || 'ISSUE';

    const [data, setData] = useState(null);

    const isReturn = receiptType === 'RETURN';

    const requestImg = './assets/requested.png';
    const rejectedImg = './assets/rejected.png';
    const approvedImg = './assets/approved.png';

    const getHeaders = () => ({
        Authorization: 'Bearer ' + token,
    });

    const loadData = async () => {
        if (!id) return;

        const res = await apiCall({
            method: 'GET',
            url: `/asset-assignments/${id}`,
            data: {
                headers: getHeaders(),
            },
        });
        if (res?.status === 'success') {
            setData(res.data);
        }
    };

    useEffect(() => {
        loadData();
    }, [id]);

    const exportPDF = async () => {
        const element = printRef.current;
        if (!element) return;

        const canvas = await html2canvas(element, {
            scale: 3,
            useCORS: true,
            backgroundColor: '#ffffff',
        });

        const imgData = canvas.toDataURL('image/png');

        const pdf = new jsPDF('p', 'mm', 'a4');
        pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
        pdf.save(`${isReturn ? 'tanda-pengembalian' : 'tanda-terima'}-${id}.pdf`);
    };

    const handlePrint = async () => {
        const element = printRef.current;
        if (!element) return;

        const canvas = await html2canvas(element, {
            scale: 3,
            useCORS: true,
            backgroundColor: '#ffffff',
        });

        const imgData = canvas.toDataURL('image/png');

        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        printWindow.document.write(`
            <html>
                <head>
                    <title>Print Receipt</title>
                    <style>
                        body {
                            margin: 0;
                            padding: 0;
                            background: #fff;
                        }

                        img {
                            width: 210mm;
                            height: 297mm;
                            display: block;
                        }

                        @page {
                            size: A4 portrait;
                            margin: 0;
                        }
                    </style>
                </head>
                <body>
                    <img src="${imgData}" />
                    <script>
                        window.onload = function() {
                            window.focus();
                            window.print();
                        };
                    </script>
                </body>
            </html>
        `);

        printWindow.document.close();
    };

    const getPositionName = (person) => {
        return person?.position?.position_name || person?.position?.name || '-';
    };

    const getSignatureImage = (status, defaultImage = approvedImg) => {
        if (status === 'Rejected') return rejectedImg;
        if (status === 'Approved') return approvedImg;
        if (status === 'Skipped') return approvedImg;
        return defaultImage;
    };

    if (!id) {
        return <Empty description="ID asset assignment tidak ditemukan" />;
    }

    const asset = data?.asset || {};
    const user = data?.user || {};
    const approval = data?.approval || {};

    const requester = user || approval?.user || {};
    const delegationUser = approval?.delegation_user || null;
    const approvalUser = approval?.approval_user || null;

    const documentTitle = isReturn ? 'TANDA PENGEMBALIAN BARANG' : 'TANDA TERIMA BARANG';

    const documentDate = isReturn
        ? data?.return_date || new Date()
        : data?.assign_date || data?.request_date || new Date();

    const receiptNo = isReturn
        ? data?.return_receipt_no || data?.receipt_no || '-'
        : data?.issue_receipt_no || data?.receipt_no || '-';

    return (
        <div style={{ padding: 24, background: '#f5f5f5' }}>
            <div style={{ maxWidth: 794, margin: '0 auto 16px', textAlign: 'right' }}>
                <Space>
                    <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                        Print
                    </Button>

                    <Button type="primary" icon={<DownloadOutlined />} onClick={exportPDF}>
                        Export PDF
                    </Button>
                </Space>
            </div>

            <Card style={{ maxWidth: 794, margin: '0 auto' }}>
                <div
                    ref={printRef}
                    style={{
                        width: 794,
                        height: 1123,
                        position: 'relative',
                        backgroundImage: `url(./assets/receipt-bg.jpeg)`,
                        backgroundSize: '100% 100%',
                        backgroundRepeat: 'no-repeat',
                        fontFamily: 'Times New Roman, serif',
                        color: '#000',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            top: 150,
                            left: 90,
                            right: 90,
                            fontSize: 16,
                            lineHeight: 1.7,
                        }}
                    >
                        <h2
                            style={{
                                textAlign: 'center',
                                textDecoration: 'underline',
                                marginBottom: 0,
                                fontSize: 22,
                            }}
                        >
                            {documentTitle}
                        </h2>

                        <div style={{ textAlign: 'center', marginBottom: 35 }}>
                            No: {receiptNo}
                        </div>

                        {!isReturn ? (
                            <>
                                <p style={{ textAlign: 'justify' }}>
                                    Pada hari {dayjs(documentDate).format('dddd')} Tanggal{' '}
                                    {dayjs(documentDate).format('DD MMMM YYYY')}, PT. Bolde Inovasi
                                    Global sudah meminjamkan seperangkat{' '}
                                    {asset?.category?.name || data?.category?.name || 'Asset'} sebagai
                                    fasilitas untuk penunjang pekerjaan, yaitu dengan detail:
                                </p>

                                <table style={{ marginTop: 20, marginBottom: 25, fontSize: 16 }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ width: 120 }}>Merek</td>
                                            <td>: {asset?.brand || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td>Type</td>
                                            <td>: {asset?.model || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td>Warna</td>
                                            <td>: {asset?.color || '-'}</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <p>Kepada karyawan,</p>
                            </>
                        ) : (
                            <>
                                <p style={{ textAlign: 'justify' }}>
                                    Pada hari {dayjs(documentDate).format('dddd')} Tanggal{' '}
                                    {dayjs(documentDate).format('DD MMMM YYYY')}, karyawan yang
                                    bersangkutan telah mengembalikan asset milik PT. Bolde Inovasi
                                    Global dengan detail sebagai berikut:
                                </p>

                                <table style={{ marginTop: 20, marginBottom: 25, fontSize: 16 }}>
                                    <tbody>
                                        <tr>
                                            <td style={{ width: 120 }}>Kode Asset</td>
                                            <td>: {asset?.asset_code || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td>Merek</td>
                                            <td>: {asset?.brand || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td>Type</td>
                                            <td>: {asset?.model || '-'}</td>
                                        </tr>
                                        <tr>
                                            <td>Kondisi</td>
                                            <td>: {asset?.condition_status || '-'}</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <p>Yang mengembalikan,</p>
                            </>
                        )}

                        <table style={{ marginTop: 15, marginBottom: 10, fontSize: 16 }}>
                            <tbody>
                                <tr>
                                    <td style={{ width: 120 }}>Nama</td>
                                    <td>: {requester?.name || '-'}</td>
                                </tr>
                                <tr>
                                    <td>Jabatan</td>
                                    <td>: {getPositionName(requester)}</td>
                                </tr>
                                <tr>
                                    <td>Departemen</td>
                                    <td>
                                        : {requester?.department?.name || requester?.division?.name || '-'}
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                        <p style={{ textAlign: 'justify' }}>
                            {isReturn
                                ? 'Dengan ditandatanganinya dokumen ini, asset telah diterima kembali oleh perusahaan dan akan diperiksa sesuai kondisi fisik serta kelengkapan yang diterima.'
                                : 'Fasilitas tersebut akan di kembalikan keperusahaan jika yang karyawan bersangkutan sudah tidak lagi aktif sebagai karyawan PT. Bolde Inovasi Global. Sebagai fasilitas perusahaan yang dapat menunjang pekerjaan, maka karyawan diharapkan untuk merawat dan menggunakannya dengan baik, jika terjadi kehilangan pada Asset yang diberikan karyawan wajib menggantinya.'}
                        </p>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginTop: 30,
                                gap: 18,
                            }}
                        >
                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <div>Requester</div>

                                <img
                                    src={requestImg}
                                    alt="request"
                                    style={{
                                        width: 85,
                                        height: 85,
                                        objectFit: 'contain',
                                        marginTop: 12,
                                    }}
                                />

                                <div
                                    style={{
                                        marginTop: 8,
                                        fontWeight: 600,
                                        textDecoration: 'underline',
                                        fontSize: 14,
                                    }}
                                >
                                    {requester?.name || '-'}
                                </div>

                                <div style={{ fontSize: 11, lineHeight: 1.3 }}>
                                    {getPositionName(requester)}
                                </div>
                            </div>

                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <div>Authorization</div>

                                {approval?.delegation_status === 'Skipped' ? (
                                    <>
                                        <img
                                            src={approvedImg}
                                            alt="skipped"
                                            style={{
                                                width: 85,
                                                height: 85,
                                                objectFit: 'contain',
                                                marginTop: 12,
                                                opacity: 0.45,
                                            }}
                                        />

                                        <div
                                            style={{
                                                marginTop: 8,
                                                fontWeight: 600,
                                                textDecoration: 'underline',
                                                fontSize: 14,
                                            }}
                                        >
                                            Skipped
                                        </div>

                                        <div style={{ fontSize: 11, lineHeight: 1.3 }}>
                                            Authorization dilewati
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <img
                                            src={getSignatureImage(approval?.delegation_status)}
                                            alt="authorization"
                                            style={{
                                                width: 85,
                                                height: 85,
                                                objectFit: 'contain',
                                                marginTop: 12,
                                            }}
                                        />

                                        <div
                                            style={{
                                                marginTop: 8,
                                                fontWeight: 600,
                                                textDecoration: 'underline',
                                                fontSize: 14,
                                            }}
                                        >
                                            {delegationUser?.name || '-'}
                                        </div>

                                        <div style={{ fontSize: 11, lineHeight: 1.3 }}>
                                            {getPositionName(delegationUser)}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div style={{ flex: 1, textAlign: 'center' }}>
                                <div>Approval</div>

                                <img
                                    src={getSignatureImage(approval?.status)}
                                    alt="approval"
                                    style={{
                                        width: 85,
                                        height: 85,
                                        objectFit: 'contain',
                                        marginTop: 12,
                                    }}
                                />

                                <div
                                    style={{
                                        marginTop: 8,
                                        fontWeight: 600,
                                        textDecoration: 'underline',
                                        fontSize: 14,
                                    }}
                                >
                                    {approvalUser?.name || '-'}
                                </div>

                                <div style={{ fontSize: 11, lineHeight: 1.3 }}>
                                    {getPositionName(approvalUser)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}

export default AssetReceiptPreview;